/* Dark mode toggle.
   =====================================================================
   Two halves. partials/color-scheme-boot.hbs is a tiny inline script in
   <head> that sets `data-theme` on <html> before first paint, from the
   stored preference or the OS setting, so a dark-mode reader never sees a
   white flash. This module runs from the deferred bundle and owns the rest:
   the toggle button (aria-pressed reflects the state; the label stays
   constant, "Toggle dark mode"), persistence, following the OS preference
   while the reader has not chosen explicitly, and the comments thread.

   The storage key is the one 1.5.x used, so a returning reader keeps the
   scheme they picked. Storage access is wrapped: Safari in private mode and
   browsers with site data blocked throw on `localStorage`, and the toggle
   must keep working without persistence. */

import { syncCommentsScheme } from './comments-scheme.js';

export const STORAGE_KEY = 'theme-preference';
const DARK_MQ = '(prefers-color-scheme: dark)';

/** The reader's explicit choice, or null when they never toggled. */
export function readPreference() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch (_error) {
    return null;
  }
}

function storePreference(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (_error) {
    /* storage blocked: the choice lasts for this page only */
  }
}

export function systemPreference() {
  return window.matchMedia(DARK_MQ).matches ? 'dark' : 'light';
}

/** Put the document, the toggle and the comments thread on one scheme. */
export function applyTheme(value, root = document) {
  root.documentElement.setAttribute('data-theme', value);
  const toggle = root.querySelector('#theme-toggle');
  if (toggle) toggle.setAttribute('aria-pressed', value === 'dark' ? 'true' : 'false');
  syncCommentsScheme(value === 'dark');
}

export default function initColorScheme({ root = document } = {}) {
  let current = readPreference() || systemPreference();
  applyTheme(current, root);

  const toggle = root.querySelector('#theme-toggle');
  const onClick = () => {
    current = current === 'dark' ? 'light' : 'dark';
    storePreference(current);
    applyTheme(current, root);
  };
  if (toggle) toggle.addEventListener('click', onClick);

  // The OS preference only speaks for readers who never chose themselves.
  const mq = window.matchMedia(DARK_MQ);
  const onChange = (event) => {
    if (readPreference()) return;
    current = event.matches ? 'dark' : 'light';
    applyTheme(current, root);
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);

  return () => {
    if (toggle) toggle.removeEventListener('click', onClick);
    if (mq.removeEventListener) mq.removeEventListener('change', onChange);
    else if (mq.removeListener) mq.removeListener(onChange);
  };
}
